import React, { useMemo } from 'react';
import { useGlobalProject } from '@/contexts/GlobalProjectContext';
import { useProjectAnalytics } from '@/hooks/useProjectAnalytics';
import { toActivitiesUrl, toExplorerUrl } from '@/utils/drillthrough';
import { EmptyState } from '@/components/analytics/EmptyState';
import LoadingState from '@/components/ui/LoadingState';
import { QualityScorecardTable } from '@/components/analytics/QualityScorecardTable';
import { AnalyticsStatusRow } from '@/components/analytics/AnalyticsStatusRow';
import { PageHeader } from '@/components/layout/PageHeader';
import { TableScaffold } from '@/components/ui/TableScaffold';
import type { QualityCheckRow } from '@/utils/analyticsCompute';

const QualityPage: React.FC = () => {
  const { selectedProject, selectedBaselineId } = useGlobalProject();
  const { loading, merged } = useProjectAnalytics();
  const errors = merged?.errors ?? [];

  const checksWithHref = useMemo((): QualityCheckRow[] => {
    if (!merged?.qualityChecks) return [];
    return merged.qualityChecks.map((c) => {
      let viewOffendersHref: string | undefined;
      switch (c.id) {
        case 'high_float_pct':
          viewOffendersHref = toActivitiesUrl({});
          break;
        case 'long_duration_pct':
          viewOffendersHref = toActivitiesUrl({});
          break;
        case 'missing_predecessors':
        case 'missing_successors':
          viewOffendersHref = toExplorerUrl('TASK');
          break;
        case 'constraints_count':
        case 'excessive_lags':
          viewOffendersHref = toExplorerUrl('TASK');
          break;
        default:
          viewOffendersHref = toActivitiesUrl({});
      }
      return { ...c, viewOffendersHref };
    });
  }, [merged?.qualityChecks]);

  const loaded = useMemo(() => ({
    schedule: !!merged,
    activities: !!(merged?.qualityChecks?.length),
    risk: !!merged,
    baseline: !!selectedBaselineId,
  }), [merged, selectedBaselineId]);

  if (!selectedProject) {
    return (
      <div className="flex flex-col h-full">
        <EmptyState />
      </div>
    );
  }

  if (loading && !merged) {
    return (
      <div className="flex flex-col h-full">
        <LoadingState message="Loading schedule quality…" size="lg" fullScreen={false} />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <PageHeader
        title="Quality scorecard"
        subtitle="Quality checks against schedule logic and data integrity."
        meta={<AnalyticsStatusRow loaded={loaded} errorCount={errors.length} errorMessages={errors} />}
      />
      {merged && (
        <TableScaffold toolbar={<span className="text-xs font-medium text-muted-foreground">Schedule Quality Checks</span>}>
          <QualityScorecardTable checks={checksWithHref} loading={loading} />
        </TableScaffold>
      )}
      {!merged && !loading && (
        <p className="text-xs text-muted-foreground">No schedule data available. Select a project with activities.</p>
      )}
    </div>
  );
};

export default QualityPage;
